import React, { useEffect, useState } from 'react';
import './Users.scss';
import Helmet from 'react-helmet';
import PageWrapper from '../../components/ui/layout/PageWrapper';
import Pagination from '../../components/ui/pagination/Pagination';
import Search from '../../components/search/Search';
import counterpart from 'counterpart';
import Table from '../../components/ui/table/Table';
import Text from '../../components/ui/text/Text';
import { connect } from 'react-redux';
import { AppState } from '../..';
import { IUsersReducerState } from '../../reducers/usersReducer';
import { fetchUsers, setUsersField, updatehUser2fa, fetchUserToken } from '../../actions/usersActions';
import { IUser } from '../../models/Users';
import Button from '../../components/ui/button/Button';
import Tooltip from '../../components/ui/tooltip/Tooltip';
import ProgressIndicator from '../../components/ui/progress-indicator/ProgressIndicator';

interface IUsers {
  users: IUsersReducerState;
  dispatch: Function;
}

const Users: React.FC<IUsers> = ({ users, dispatch }) => {
  const [output, setOutput] = useState(<div />);
  const { searchedUsers, usersPaginated, hasUsers } = users;

  useEffect(() => {
    dispatch(fetchUsers(1));
  }, []);

  const renderSearchBar = () => {
    return (
      <div className="users__search">
        <Search
          value={searchedUsers}
          placeholder="USERS_SEARCH_PLACEHOLDER"
          onChange={(evt: any) => dispatch(setUsersField('searchedUsers', evt.target.value))}
          onSearch={() => dispatch(fetchUsers(1, undefined, searchedUsers))}
        />
        <Pagination
          className="users__search__pagination"
          pageCount={usersPaginated.pageCount}
          isVisible={usersPaginated.pageCount > 1}
          onPageChange={({ selected }) => dispatch(fetchUsers(selected + 1, undefined, searchedUsers))}
        />
      </div>
    );
  };

  const turnOff2fa = async (userId: number) => {
    await dispatch(updatehUser2fa(userId));
    dispatch(fetchUsers(usersPaginated.currentPage, undefined, searchedUsers));    
  };

  const renderUsersTable = () => {
    return (
      <Table className="users__table">
        <thead>
          <tr>
            <Text content="USERS_TABLE_EMAIL" component="th" noMargin />
            <Text content="USERS_TABLE_FULL_NAME" component="th" noMargin />
            <Text content="USERS_TABLE_CONTACT_NUMBER" component="th" noMargin />
            <Text content="USERS_TABLE_COMPANIES" component="th" noMargin />
            <th />
          </tr>
        </thead>
        <tbody>
          {usersPaginated.results && usersPaginated.results.map((data, idx) => (
            <tr key={idx}>
              <Text content={data.email} component="td" noMargin shouldTranslate={false} />
              <Text content={`${data.firstName} ${data.lastName}`} component="td" noMargin shouldTranslate={false} />
              <Text content={data.phoneNumber} component="td" noMargin shouldTranslate={false} />
              <td className="users__table__companies">
                {data.companyRoles.map((company, idx) => {
                  const tooltipId = `${company.companyName}-${Math.floor(Math.random() * 100)}`;
                  return (
                    <div data-tip data-for={tooltipId} key={tooltipId}>
                      <Text content={`${company.companyName}${idx !== data.companyRoles.length - 1 ? ',' : ''}`} component="span" shouldTranslate={false} noMargin></Text>
                      <Tooltip className="users__table__tooltip" type="blue" id={tooltipId} place="bottom">
                        <Text content={company.roles.join(" ")} shouldTranslate={false} noMargin></Text>
                      </Tooltip>
                    </div>
                  );
                })}
              </td>

              <td className="users__table__actions">
                <Text className="users__table__support-code" content={data.phoneSupportCode} component="div" noMargin shouldTranslate={false} />
                <Button 
                  disabled={!data.is2faActive || data.isArchived} 
                  id="2fa" 
                  buttonType="add" 
                  title="USERS_BUTTON_TURN_OFF_2FA" 
                  isFullWidth 
                  onClick={() => turnOff2fa(data.userId)} 
                />
                {/* TODO: User access account button */}
                <Button 
                  id="access-account" 
                  buttonType="add" 
                  title="USERS_BUTTON_ACCESS_ACCOUNT" 
                  isFullWidth
                  disabled={data.isArchived}
                  isVisible={data.isImpersonationEnabled}
                  onClick={() => dispatch(fetchUserToken(data, false))}
                />
                <Button 
                  id="force-access-account" 
                  buttonType="error" 
                  title="USERS_BUTTON_FORCE_ACCESS_ACCOUNT" 
                  isFullWidth
                  disabled={data.isArchived}
                  isVisible={!data.isImpersonationEnabled}
                  onClick={() => dispatch(fetchUserToken(data, true))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  if(!hasUsers) {
    return <ProgressIndicator color="blue" coverage='full-content' />
  }

  return (
    <div className="users">
      <Helmet title={counterpart('USERS_TITLE')} />

      <PageWrapper>
        {renderSearchBar()}
        {renderUsersTable()}
      </PageWrapper>
    </div>
  );
};

export default connect((state: AppState) => ({ users: state.users }))(Users);
